import React, { Component } from 'react';

import { FormGroup, UncontrolledTooltip } from 'reactstrap';
import { Field, reduxForm } from 'redux-form';

import AddAppAlert from '../alert/AddAppAlert';
import Loader from '../loader/Loader';
import MagazineCreateButton from '../formFields/MagazineCreateButton';
import AutoLocaleCheckbox from '../formFields/AutoLocaleCheckbox';
import renderSelectField from '../formFields/Select';
import renderMultiSelectField from '../formFields/MultiSelect';
import renderRadioButtonField from '../formFields/RadioButton';
import { validate } from '../../utils/form';

const allCategoriesLabel = (
  <span>
    All available categories
    <div id="all-categories" className="info-tooltip">
      ?
    </div>
    <UncontrolledTooltip
      placement="right"
      target="all-categories"
      className="info-tooltip"
    >
      Create a magazine out of all the available content <br />
      (Less recommended)
    </UncontrolledTooltip>
  </span>
);

class InstantMagazineForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      country: '',
      languages: [],
      categories: []
    };

    this.onAppChange = this.onAppChange.bind(this);
    this.onCountryChange = this.onCountryChange.bind(this);
    this.onLanguageChange = this.onLanguageChange.bind(this);
  }

  componentDidMount() {
    const { appsLoading, fetchAllApps } = this.props;
    if (appsLoading) {
      fetchAllApps();
    }
  }

  componentDidUpdate() {
    let { country } = this.state;
    const {
      data: { countries = [], UnitedState },
      formValues = {},
      changeFieldValue
    } = this.props;
    if (!country && countries.length > 0) {
      country = UnitedState || countries[0];
      changeFieldValue('country', country);
      this.onCountryChange(country);
    } else if (country && countries.length === 0) {
      changeFieldValue('country', null);
      this.onCountryChange({});
    }

    if (formValues.allCategories) {
      changeFieldValue('categories', []);
    }
  }

  onAppChange(selectedApp) {
    this.props.fetchMediaData(selectedApp.value);
  }

  onCountryChange({ value }) {
    const {
      data: { languages = {}, categoriesData = {} },
      changeFieldValue,
      resetURL
    } = this.props;
    const languageValues = languages[value] || [];
    const language = languageValues[0] || null;

    this.setState(() => ({
      languages: languageValues,
      country: value,
      categories:
        categoriesData[value] && language
          ? categoriesData[value][language.value]
          : []
    }));
    changeFieldValue('language', language);
    changeFieldValue('categories', []);
    resetURL();
  }

  onLanguageChange({ value }) {
    const {
      data: { categoriesData = {} },
      changeFieldValue,
      resetURL
    } = this.props;
    const { country } = this.state;

    this.setState(() => ({
      categories: categoriesData[country] ? categoriesData[country][value] : []
    }));
    changeFieldValue('categories', []);
    resetURL();
  }

  render() {
    const {
      appsLoading,
      apps,
      url,
      loading,
      data: { countries },
      handleSubmit,
      generating,
      hasCategory,
      formValues = {}
    } = this.props;
    const { languages, categories = [], country } = this.state;
    const hasNoApps = !appsLoading && apps.length === 0;
    const isAppNotSelected = !formValues.app;
    const isAutoLocale = !!formValues.autoLocale;

    return (
      <Loader loaded={appsLoading}>
        {hasNoApps && <AddAppAlert />}
        <div className="content-container">
          <div className="page-header">
            <div className="page-title">Create your Instant Magazine</div>
            <div id="what-is">What is instant magazine?</div>
            <UncontrolledTooltip
              placement="bottom"
              target="what-is"
              className="what-is-tooltip"
            >
              Gain access to your customized magazine in seconds with Instant
              Click. No need to study API docs or implement a third-party SDK.
              Change magazines as often as you like and create many custom
              content filters.
            </UncontrolledTooltip>
          </div>
          <form className="form form--create-magazine" onSubmit={handleSubmit}>
            <ol>
              <li>
                <div className="form__form-group select-app">
                  <span className="form__form-group-label">Select an App:</span>
                  <Field
                    name="app"
                    component={renderSelectField}
                    placeholder="Select App"
                    onChange={this.onAppChange}
                    options={apps.map(app => ({ label: app.name, value: app }))}
                    isDisabled={hasNoApps}
                    className="form__form-group-select"
                  />
                </div>
              </li>
              <li>
                <div className="form__form-group select-country-auto">
                  <div className="form__form-group-label">
                    Select country and Language:
                  </div>
                  <div>
                    <AutoLocaleCheckbox />
                  </div>
                </div>
                <div className="form__form-group select-country-manually">
                  <Field
                    name="country"
                    placeholder="Select Country"
                    component={renderSelectField}
                    onChange={this.onCountryChange}
                    options={countries}
                    label="Or manually select Country"
                    isDisabled={isAppNotSelected || isAutoLocale}
                    className="form__form-group-select"
                  />
                  <Field
                    name="language"
                    placeholder="Select Language"
                    component={renderSelectField}
                    onChange={this.onLanguageChange}
                    options={languages}
                    label="And Language"
                    isDisabled={isAppNotSelected || isAutoLocale}
                    className="form__form-group-select"
                  />
                </div>
              </li>
              <li>
                <FormGroup>
                  <div className="form__form-group-label">
                    Choose the magazine categories:
                  </div>
                  <Field
                    name="allCategories"
                    component={renderRadioButtonField}
                    label={allCategoriesLabel}
                    radioValue={true}
                  />
                  <Field
                    name="allCategories"
                    component={renderRadioButtonField}
                    label="Select categories"
                    radioValue={false}
                  />
                  <Field
                    name="categories"
                    component={renderMultiSelectField}
                    placeholder="Tap to view and select categories, or type a category"
                    options={categories.map(value => ({ label: value, value }))}
                    isDisabled={formValues.allCategories || loading}
                    className="form__form-group-select"
                  />
                </FormGroup>
              </li>
              <li>
                <MagazineCreateButton
                  url={url}
                  disabled={
                    loading ||
                    isAppNotSelected ||
                    !hasCategory ||
                    generating ||
                    (!isAutoLocale && !country)
                  }
                />
              </li>
            </ol>
          </form>
        </div>
      </Loader>
    );
  }
}

export default reduxForm({
  form: 'InstantMagazineForm',
  validate
})(InstantMagazineForm);



// WEBPACK FOOTER //
// ./src/components/instantMagazine/InstantMagazineForm.js